/**
 * Input Validation and Sanitization Utility for Pentest Module
 * 
 * This utility provides comprehensive input validation and sanitization to prevent:
 * - Cross-Site Scripting (XSS) attacks
 * - SQL Injection attacks
 * - HTML injection
 * - Script injection
 * - Path traversal
 * - Command injection
 * 
 * Security Features:
 * - Character whitelisting for different input types
 * - HTML and script tag detection and removal
 * - SQL wildcard character detection
 * - Special character sanitization
 * - Maximum length enforcement
 */

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  errors: string[];
}

/**
 * Input validation configuration
 */
export interface ValidationConfig {
  maxLength?: number;
  minLength?: number;
  allowSpecialChars?: boolean;
  allowNumbers?: boolean;
  allowSpaces?: boolean;
  allowNewlines?: boolean;
  customPattern?: RegExp;
  preventXSS?: boolean;
  preventSQLInjection?: boolean;
}

// Dangerous characters and patterns
const XSS_PATTERNS = [
  /<script[^>]*>.*?<\/script>/gi,
  /<iframe[^>]*>.*?<\/iframe>/gi,
  /<object[^>]*>.*?<\/object>/gi,
  /<embed[^>]*>/gi,
  /<applet[^>]*>.*?<\/applet>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi, // Event handlers like onclick, onerror, etc.
  /<img[^>]*on\w+/gi,
  /<svg[^>]*on\w+/gi,
  /eval\s*\(/gi,
  /expression\s*\(/gi,
  /<[^>]*style[^>]*=.*javascript/gi,
];

const SQL_INJECTION_PATTERNS = [
  /(\bOR\b|\bAND\b)\s+\d+\s*=\s*\d+/gi,
  /\bUNION\b.*\bSELECT\b/gi,
  /\bEXEC\b.*\(/gi,
  /\bEXECUTE\b.*\(/gi,
  /\bDROP\b.*\bTABLE\b/gi,
  /\bDELETE\b.*\bFROM\b/gi,
  /\bINSERT\b.*\bINTO\b/gi,
  /\bUPDATE\b.*\bSET\b/gi,
  /;.*(\bDROP\b|\bDELETE\b|\bINSERT\b|\bUPDATE\b)/gi,
  /--/g,
  /\/\*/g,
  /\*\//g,
  /xp_/gi,
  /sp_/gi,
];

// Dangerous HTML tags
const DANGEROUS_HTML_TAGS = [
  'script',
  'iframe',
  'object',
  'embed',
  'applet',
  'link',
  'style',
  'meta',
  'base',
  'form',
  'img',
  'svg',
  'video',
  'audio',
];

// SQL wildcard and special characters
const SQL_WILDCARDS = ['%', '_', '[', ']', '^', '-'];
const SQL_SPECIAL_CHARS = [';', '--', '/*', '*/', 'xp_', 'sp_'];

/**
 * Sanitize input by removing dangerous characters and patterns
 */
export function sanitizeInput(input: string, config: ValidationConfig = {}): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Prevent XSS by default
  if (config.preventXSS !== false) {
    // Remove HTML tags
    sanitized = removeHtmlTags(sanitized);
    
    // Remove dangerous XSS patterns
    XSS_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    // Encode dangerous characters
    sanitized = sanitized
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  // Prevent SQL Injection by default
  if (config.preventSQLInjection !== false) {
    SQL_INJECTION_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    // Remove SQL wildcards and special characters
    SQL_WILDCARDS.forEach(char => {
      sanitized = sanitized.replace(new RegExp(`\\${char}`, 'g'), '');
    });
  }

  // Enforce maximum length
  if (config.maxLength && sanitized.length > config.maxLength) {
    sanitized = sanitized.substring(0, config.maxLength);
  }

  return sanitized.trim();
}

/**
 * Remove HTML tags from input
 */
function removeHtmlTags(input: string): string {
  // Remove all HTML tags
  return input.replace(/<[^>]*>/g, '');
}

/**
 * Validate input against configuration rules
 */
export function validateInput(input: string, config: ValidationConfig = {}): ValidationResult {
  const errors: string[] = [];
  let sanitizedValue = sanitizeInput(input, config);

  // Check minimum length
  if (config.minLength && sanitizedValue.length < config.minLength) {
    errors.push(`Input must be at least ${config.minLength} characters long`);
  }

  // Check maximum length
  if (config.maxLength && sanitizedValue.length > config.maxLength) {
    errors.push(`Input must not exceed ${config.maxLength} characters`);
  }

  // Check for special characters if not allowed
  if (config.allowSpecialChars === false) {
    const specialCharsPattern = /[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?`~]/;
    if (specialCharsPattern.test(sanitizedValue)) {
      errors.push('Special characters are not allowed');
    }
  }

  // Check custom pattern if provided
  if (config.customPattern && !config.customPattern.test(sanitizedValue)) {
    errors.push('Input does not match the required pattern');
  }

  // Check for XSS attempts
  if (config.preventXSS !== false && containsXSSAttempt(input)) {
    errors.push('Input contains potentially malicious content');
  }

  // Check for SQL injection attempts
  if (config.preventSQLInjection !== false && containsSQLInjectionAttempt(input)) {
    errors.push('Input contains potentially malicious SQL patterns');
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors,
  };
}

/**
 * Check if input contains XSS attempt
 */
function containsXSSAttempt(input: string): boolean {
  // Check for any angle brackets (< or >) which are used in HTML/XSS attacks
  if (/</.test(input) || />/.test(input)) return true;
  
  // Check for script tags
  if (/<script/gi.test(input)) return true;
  
  // Check for event handlers
  if (/on\w+\s*=/gi.test(input)) return true;
  
  // Check for javascript: protocol
  if (/javascript:/gi.test(input)) return true;
  
  // Check for dangerous tags
  for (const tag of DANGEROUS_HTML_TAGS) {
    const tagPattern = new RegExp(`<${tag}`, 'gi');
    if (tagPattern.test(input)) return true;
  }
  
  // Check for encoded script attempts
  if (/(%3C|&lt;)script/gi.test(input)) return true;
  if (/(%3E|&gt;)/gi.test(input) && /(%3C|&lt;)/gi.test(input)) return true;
  
  return false;
}

/**
 * Check if input contains SQL injection attempt
 */
function containsSQLInjectionAttempt(input: string): boolean {
  // Check for SQL injection patterns
  for (const pattern of SQL_INJECTION_PATTERNS) {
    if (pattern.test(input)) return true;
  }
  
  // Check for SQL comments
  if (/--/.test(input) || /\/\*/.test(input)) return true;
  
  // Check for multiple SQL wildcards (suspicious)
  const wildcardCount = SQL_WILDCARDS.reduce((count, char) => {
    return count + (input.match(new RegExp(`\\${char}`, 'g'))?.length || 0);
  }, 0);
  
  if (wildcardCount > 2) return true;
  
  return false;
}

/**
 * Validate project name
 */
export function validateProjectName(name: string): ValidationResult {
  return validateInput(name, {
    minLength: 1,
    maxLength: 100,
    allowSpecialChars: false,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: false,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate project description
 */
export function validateProjectDescription(description: string): ValidationResult {
  return validateInput(description, {
    minLength: 0,
    maxLength: 500,
    allowSpecialChars: true, // Allow some special chars in description
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: true,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate artifact name
 */
export function validateArtifactName(name: string): ValidationResult {
  return validateInput(name, {
    minLength: 1,
    maxLength: 100,
    allowSpecialChars: false,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: false,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate finding name
 */
export function validateFindingName(name: string): ValidationResult {
  return validateInput(name, {
    minLength: 1,
    maxLength: 200,
    allowSpecialChars: false,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: false,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate analysis name
 */
export function validateAnalysisName(name: string): ValidationResult {
  return validateInput(name, {
    minLength: 1,
    maxLength: 100,
    allowSpecialChars: false,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: false,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate analysis description
 */
export function validateAnalysisDescription(description: string): ValidationResult {
  return validateInput(description, {
    minLength: 0,
    maxLength: 500,
    allowSpecialChars: true,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: true,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate chat message
 */
export function validateChatMessage(message: string): ValidationResult {
  return validateInput(message, {
    minLength: 1,
    maxLength: 2000,
    allowSpecialChars: true,
    allowNumbers: true,
    allowSpaces: true,
    allowNewlines: true,
    preventXSS: true,
    preventSQLInjection: true,
  });
}

/**
 * Validate Burp Suite host configuration
 */
export function validateBurpSuiteHost(host: string): ValidationResult {
  // Allow empty value (field is optional)
  if (!host || host.trim() === '') {
    return {
      isValid: true,
      sanitizedValue: '',
      errors: [],
    };
  }

  const result = validateInput(host, {
    minLength: 1,
    maxLength: 255,
    allowSpecialChars: true,
    allowNumbers: true,
    allowSpaces: false,
    allowNewlines: false,
    preventXSS: true,
    preventSQLInjection: true,
  });

  // Additional validation for host format (localhost, IP, or domain)
  const hostPattern = /^(localhost|(\d{1,3}\.){3}\d{1,3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+)(:\d{1,5})?$/;
  if (result.isValid && !hostPattern.test(result.sanitizedValue)) {
    result.isValid = false;
    result.errors.push('Invalid host format. Use localhost, IP address, or domain name.');
  }

  return result;
}

/**
 * Sanitize filename to prevent path traversal
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  // Remove path traversal attempts
  let sanitized = filename.replace(/\.\./g, '');
  sanitized = sanitized.replace(/[\/\\]/g, '');
  
  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');
  
  // Remove dangerous characters
  sanitized = sanitized.replace(/[<>:"|?*]/g, '');
  
  // Remove control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
  
  return sanitized.trim();
}

/**
 * Check if a string is safe (no XSS or SQL injection attempts)
 */
export function isSafeInput(input: string): boolean {
  return !containsXSSAttempt(input) && !containsSQLInjectionAttempt(input);
}

/**
 * Escape HTML entities
 */
export function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '/': '&#x2F;',
  };
  return text.replace(/[&<>"'/]/g, (char) => map[char]);
}

/**
 * Unescape HTML entities (for display purposes only)
 */
export function unescapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&#x27;': "'",
    '&#x2F;': '/',
  };
  return text.replace(/&[^;]+;/g, (entity) => map[entity] || entity);
}

/**
 * Whitelist alphanumeric characters only
 */
export function whitelistAlphanumeric(input: string, allowSpaces: boolean = true): string {
  const pattern = allowSpaces ? /[^a-zA-Z0-9 ]/g : /[^a-zA-Z0-9]/g;
  return input.replace(pattern, '');
}

/**
 * Blacklist dangerous characters
 */
export function blacklistDangerousChars(input: string): string {
  // Remove characters commonly used in attacks
  const dangerousChars = /[<>{}%&$\\/'"`;|]/g;
  return input.replace(dangerousChars, '');
}

/**
 * Validate URL to prevent SSRF and malicious redirects
 */
export function validateUrl(url: string): ValidationResult {
  const errors: string[] = [];
  
  try {
    const urlObj = new URL(url);
    
    // Check protocol
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      errors.push('Only HTTP and HTTPS protocols are allowed');
    }
    
    // Prevent local network access
    const hostname = urlObj.hostname.toLowerCase();
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)
    ) {
      errors.push('Access to local network addresses is not allowed');
    }
    
    return {
      isValid: errors.length === 0,
      sanitizedValue: url,
      errors,
    };
  } catch (e) {
    return {
      isValid: false,
      sanitizedValue: '',
      errors: ['Invalid URL format'],
    };
  }
}
